import * as React from "react";
import * as signalR from "@microsoft/signalr";

import { useIsAuthenticated } from "@azure/msal-react";

import DataContext from "../../common/DataContext";
import NotificationContext from "../../common/NotificationContext";

import { LogMessageType } from "../../api/types";
import { NotificationContextType } from "../../common/NotificationContext";
import { getActiveAccountAccessToken } from "../../common/msal";

type PropsType = React.PropsWithChildren & {};

export default function NotificationProvider(props: PropsType) {
  const isAuthenticated = useIsAuthenticated();

  const dataContext = React.useContext(DataContext);

  const [notifications, setNotifications] = React.useState<LogMessageType[]>(
    []
  );

  // custom factory for fetching oauth tokens for use with the signalr
  // connection
  async function accessTokenFactory() {
    const token = await getActiveAccountAccessToken();
    return token.accessToken;
  }

  // build and configure the hub client
  function buildHubConnection() {
    const hubConnectionOptions: signalR.IHttpConnectionOptions = {
      accessTokenFactory
    };

    const hubConnection = new signalR.HubConnectionBuilder()
      .withAutomaticReconnect()
      .withUrl(
        `${process.env.WIB_API_URL}/signalr/system-events`,
        hubConnectionOptions
      )
      .build();

    hubConnection.on("ReceiveLogMessage", writeNotification);

    hubConnection.on(
      "UpdateAttributeMetadatas",
      dataContext.fetchAttributeMetadatas
    );

    return hubConnection;
  }

  // effect that runs when the notification provider finally mounts and is
  // ready
  function onInitialLoadEffect() {
    const hubConnection = buildHubConnection();

    if (isAuthenticated) {
      const onInitialLoad = async () => {
        try {
          const token = await getActiveAccountAccessToken();
          if (token && token.accessToken) {
            await hubConnection.start();
          }
        } catch (err) {
          // Retry once after a short delay if handshake fails
          setTimeout(async () => {
            try {
              await hubConnection.start();
            } catch {}
          }, 3000);
        }
      };

      onInitialLoad();
    }

    return () => {
      hubConnection.stop();
    };
  }

  function writeNotification(message: LogMessageType) {
    setNotifications((previousState) => [message, ...previousState]);
  }

  React.useEffect(onInitialLoadEffect, [isAuthenticated]);

  const notificationContextValue: NotificationContextType = {
    write: writeNotification,
    notifications
  };

  return (
    <NotificationContext.Provider value={notificationContextValue}>
      {props.children}
    </NotificationContext.Provider>
  );
}
